import React, { ReactElement } from 'react';
import { connect } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';
import { RootState } from '../../../../redux/store';
import { diagramApi } from '../../../../redux/diagram/api';
import { getViewsState } from '../../../../redux/views/slice';
import { diagramSelectors } from '../../../../redux/diagram/slice';
import { useSessionUserContext } from '../../../../hooks/use-session-user-context';
import { MainRoutes } from '../../../../constants/routes';
import { retrieveVisibleNodes } from '../../utils';
import { ConnectedDCloudSessionNodes } from './dCloudContainer';
import { ConnectedPublicSessionNodes } from './publicContainer';
import { Nodes } from '.';

export type DiagramNodesContainerProps = Omit<
  ReturnType<typeof mapStateToProps>,
  'isExporting'
> &
  Partial<IsExporting>;

export function DiagramNodesContainer({
  isReadOnly,
  isAddingBox,
  isAddingLine,
  isAddingText,
  isSnapToGridEnabled,
  isAddingLogicalConnection,
  isExporting,
}: DiagramNodesContainerProps): ReactElement {
  const { uid: topologyUid } = useParams<TopologyUid>();
  const { data } = diagramApi.endpoints.getDiagram.useQueryState(topologyUid);

  if (data === undefined) {
    return <Redirect to={MainRoutes.GenericError} />;
  }

  const nodes = isExporting ? retrieveVisibleNodes(data.nodes) : data.nodes;

  return (
    <Nodes
      nodes={nodes}
      isReadOnly={isReadOnly}
      isAddingBox={isAddingBox}
      isAddingLine={isAddingLine}
      isAddingText={isAddingText}
      isSnapToGridEnabled={isSnapToGridEnabled}
      isAddingLogicalConnection={isAddingLogicalConnection}
    />
  );
}

export function SessionNodesContainer({
  isReadOnly,
  isAddingBox,
  isAddingLine,
  isAddingText,
}: DiagramNodesContainerProps): ReactElement {
  const { accessKey, sessionUid, versionUid } = useSessionUserContext();

  if (accessKey) {
    return (
      <ConnectedPublicSessionNodes
        isReadOnly={isReadOnly}
        isAddingBox={isAddingBox}
        isAddingLine={isAddingLine}
        isAddingText={isAddingText}
        accessKey={accessKey}
        sessionUid={sessionUid}
        versionUid={versionUid}
        isSnapToGridEnabled={false}
        isAddingLogicalConnection={false}
      />
    );
  }

  return (
    <ConnectedDCloudSessionNodes
      isReadOnly={isReadOnly}
      isAddingBox={isAddingBox}
      isAddingLine={isAddingLine}
      isAddingText={isAddingText}
      sessionUid={sessionUid}
      versionUid={versionUid}
      isSnapToGridEnabled={false}
      isAddingLogicalConnection={false}
    />
  );
}

const mapStateToProps = (state: RootState) => ({
  isAddingBox: diagramSelectors.getIsAddingBox(state),
  isAddingLine: diagramSelectors.getIsAddingLine(state),
  isAddingLogicalConnection:
    diagramSelectors.getIsAddingLogicalConnection(state),
  isAddingText: diagramSelectors.getIsAddingText(state),
  isExporting: diagramSelectors.getIsExporting(state),
  isReadOnly: getViewsState(state).isReadOnly,
  isSnapToGridEnabled: diagramSelectors.getIsSnapToGridEnabled(state),
});

export const ConnectedSessionNodes = connect(mapStateToProps)(
  SessionNodesContainer,
);

export const ConnectedDiagramNodes = connect(mapStateToProps)(
  DiagramNodesContainer,
);
